import Layer from "../api/model/snapshots/layers/Layer";
import Rect from "../api/model/snapshots/layers/Rect";
import Fill from "../api/model/snapshots/layers/Fill";
import Border from "../api/model/snapshots/layers/Border";
import Shadow from "../api/model/snapshots/layers/Shadow";
import Blur from "../api/model/snapshots/layers/Blur";
import Layout, { LayoutAlignment } from "../api/model/snapshots/layers/Layout";
import CornerRadius from "../api/model/snapshots/layers/CornerRadius";
import ZeplinBlendMode from "../api/model/snapshots/layers/ZeplinBlendMode";
import RangedTextStyle from "../api/model/textStyles/RangedTextStyle";
import Constraints from "../api/model/snapshots/layers/Constraints";
import LayerHash from "../api/model/snapshots/layers/LayerHash";

import BasicRecord from "./BasicRecord";
import { equals as rectEquals, RectDiffOptions } from "./layer/rect";
import { equals as fillEquals } from "./layer/fill";
import { equals as layerHashEquals } from "./layer/layerHash";
import { equals as borderEquals } from "./layer/border";
import { equals as shadowEquals } from "./layer/shadow";
import { equals as blurEquals } from "./layer/blur";
import { equals as cornerRadiusEquals } from "./layer/cornerRadius";
import { equals as textStyleEquals } from "./layer/textStyle";
import { equals as layoutEquals } from "./layer/layout";
import { equals as constraintsEquals } from "./layer/constraints";
import InspectLayer from "../model/InspectLayer";

import LayerDiffs, { PropertyDiff } from "../model/LayerDiffs";
import { KeysOfType } from "./UtilityTypes";

type PropertyDiffOptions = {
    add?: boolean;
    remove?: boolean;
    change?: boolean;
};

function rectDiffWithLayoutAndConstraints(
    {
        rect,
        layout,
        constraints,
        childCount
    }: {
        rect: Rect;
        layout?: Layout;
        constraints?: Constraints;
        childCount: number;
    },
    {
        rect: otherRect,
        layout: otherLayout,
        constraints: otherConstraints,
        childCount: otherChildCount
    }: {
        rect: Rect;
        layout?: Layout;
        constraints?: Constraints;
        childCount: number;
    },
    options: LayerDiffOptions,
    diffs: LayerDiffs
) {
    const rectDiffOptions: RectDiffOptions = { ignorePos: options.ignorePos };
    const removedChild = childCount < otherChildCount;
    if (layout && otherLayout) {
        if (constraints?.horizontal.sizingMode !== "fixed" && otherConstraints?.horizontal.sizingMode !== "fixed" && !removedChild) {
            rectDiffOptions.ignoreWidth = true;
        }
        if (constraints?.vertical.sizingMode !== "fixed" && otherConstraints?.vertical.sizingMode !== "fixed" && !removedChild) {
            rectDiffOptions.ignoreHeight = true;
        }
    }

    propertyDiff(rect, otherRect, diffs, "rect", (firstRect, secondRect) => rectEquals(firstRect, secondRect, rectDiffOptions));
}

function fillsDiff(fills: Fill[], otherFills: Fill[], diffs: LayerDiffs) {
    propertyArrayDiff(fills, otherFills, diffs, "fills", fillEquals, { add: true, remove: true, change: true });
}

function bordersDiff(borders: Border[], otherBorders: Border[], diffs: LayerDiffs) {
    propertyArrayDiff(borders, otherBorders, diffs, "borders", borderEquals, { add: true, remove: true, change: true });
}

function shadowsDiff(shadows: Shadow[], otherShadows: Shadow[], diffs: LayerDiffs) {
    propertyArrayDiff(shadows, otherShadows, diffs, "shadows", shadowEquals, { add: true, remove: true, change: true });
}

function blurDiff(blur: Blur | undefined, otherBlur: Blur | undefined, diffs: LayerDiffs) {
    propertyDiff(blur, otherBlur, diffs, "blur", blurEquals, { add: true, remove: true, change: true });
}

function rotationDiff(rotation: number | undefined, otherRotation: number | undefined, diffs: LayerDiffs) {
    propertyDiff(rotation, otherRotation, diffs, "rotation", strictEquals);
}

function borderRadiusDiff(borderRadius: number, otherBorderRadius: number, diffs: LayerDiffs) {
    propertyDiff(borderRadius, otherBorderRadius, diffs, "borderRadius", strictEquals);
}

function cornerRadiusDiff(
    cornerRadius: CornerRadius | undefined, otherCornerRadius: CornerRadius | undefined, diffs: LayerDiffs
) {
    propertyDiff(cornerRadius, otherCornerRadius, diffs, "cornerRadius", cornerRadiusEquals, { add: true, remove: true, change: true });
}

function opacityDiff(opacity: number, otherOpacity: number, diffs: LayerDiffs) {
    propertyDiff(opacity, otherOpacity, diffs, "opacity", strictEquals);
}

function blendModeDiff(
    blendMode: ZeplinBlendMode | undefined, otherBlendMode: ZeplinBlendMode | undefined, diffs: LayerDiffs
) {
    propertyDiff(blendMode, otherBlendMode, diffs, "blendMode", strictEquals);
}

function contentDiff(content: string | undefined, otherContent: string | undefined, diffs: LayerDiffs) {
    propertyDiff(content, otherContent, diffs, "content", strictEquals);
}

function exportableDiff(exportable: boolean | undefined, otherExportable: boolean | undefined, diffs: LayerDiffs) {
    propertyDiff(exportable, otherExportable, diffs, "exportable", strictEquals);
}

function bitmapImageHashDiff(
    bitmapImageHash: string | undefined, otherBitmapImageHash: string | undefined, diffs: LayerDiffs
) {
    propertyDiff(bitmapImageHash, otherBitmapImageHash, diffs, "bitmapImageHash", strictEquals);
}

function layerHashesDiff(
    layerHashes: LayerHash[] | undefined, otherLayerHashes: LayerHash[] | undefined, diffs: LayerDiffs
) {
    propertyArrayDiff(layerHashes, otherLayerHashes, diffs, "layerHashes", layerHashEquals);
}

function layoutDiff(layout: Layout | undefined, otherLayout: Layout | undefined, diffs: LayerDiffs) {
    propertyDiff(layout, otherLayout, diffs, "layout", layoutEquals);
}

function constraintsDiff(
    constraints: Constraints | undefined, otherConstraints: Constraints | undefined, diffs: LayerDiffs
) {
    propertyDiff(constraints, otherConstraints, diffs, "constraints", constraintsEquals);
}

function textStylesDiff(
    textStyles: RangedTextStyle[] | undefined, otherTextStyles: RangedTextStyle[] | undefined, diffs: LayerDiffs
) {
    const checkRange = textStyles?.length !== 1;
    propertyArrayDiff(
        textStyles,
        otherTextStyles,
        diffs,
        "textStyles",
        (textStyle, otherTextStyle) => textStyleEquals(textStyle, otherTextStyle, checkRange),
        { add: true, remove: true, change: true }
    );
}

function layoutAlignmentDiff(
    layoutAlignment: LayoutAlignment | undefined, otherLayoutAlignment: LayoutAlignment | undefined, diffs: LayerDiffs
) {
    propertyDiff(layoutAlignment, otherLayoutAlignment, diffs, "layoutAlignment", strictEquals, { add: true, remove: true, change: true });
}

function layoutGrowDiff(layoutGrow: number | undefined, otherLayoutGrow: number | undefined, diffs: LayerDiffs) {
    propertyDiff(layoutGrow, otherLayoutGrow, diffs, "layoutGrow", strictEquals, { add: true, remove: true, change: true });
}

function strictEquals<T>(first: T, second: T) {
    return first === second;
}

// eslint-disable-next-line max-params
export function propertyArrayDiff<
    T,
    PropertyName extends KeysOfType<LayerDiffs, BasicRecord<PropertyDiff<T>> | undefined>
>(
    property: T[] | undefined,
    otherProperty: T[] | undefined,
    diffs: { [k in PropertyName]?: BasicRecord<PropertyDiff<T>> },
    propertyName: PropertyName,
    equals: (property: T, otherProperty: T) => boolean,
    options: PropertyDiffOptions = {
        add: false,
        remove: false,
        change: true
    }
) {
    if ((!property || property.length === 0) && (!otherProperty || otherProperty.length === 0)) {
        return;
    }

    const propertyDiffs: BasicRecord<PropertyDiff<T>> = {};

    const propertyArr = property || [];
    const otherPropertyArr = otherProperty || [];

    const maxLength = Math.max(propertyArr.length, otherPropertyArr.length);
    for (let i = 0; i < maxLength; i++) {
        if (i < propertyArr.length && i >= otherPropertyArr.length) {
            if (options.add) {
                propertyDiffs[i] = { add: propertyArr[i] };
            }
        } else if (i >= propertyArr.length && i < otherPropertyArr.length) {
            if (options.remove) {
                propertyDiffs[i] = { remove: otherPropertyArr[i] };
            }
        } else if (!equals(propertyArr[i], otherPropertyArr[i])) {
            if (options.change) {
                propertyDiffs[i] = { change: { newValues: propertyArr[i], oldValues: otherPropertyArr[i] } };
            }
        }
    }

    if (Object.keys(propertyDiffs).length) {
        diffs[propertyName] = propertyDiffs;
    }
}

// eslint-disable-next-line max-params
export function propertyDiff<
    T,
    PropertyName extends KeysOfType<LayerDiffs, PropertyDiff<T> | undefined>
>(
    property: T | undefined,
    otherProperty: T | undefined,
    diffs: { [k in PropertyName]?: PropertyDiff<T> },
    propertyName: PropertyName,
    equals: (property: T, otherProperty: T) => boolean,
    options: PropertyDiffOptions = {
        add: false,
        remove: false,
        change: true
    }
) {
    if (property === undefined && otherProperty === undefined) {
        return;
    }

    if (property !== undefined && otherProperty === undefined) {
        if (options.add) {
            diffs[propertyName] = { add: property };
        }
        return;
    }

    if (property === undefined && otherProperty !== undefined) {
        if (options.remove) {
            diffs[propertyName] = { remove: otherProperty };
        }
        return;
    }

    if (!equals(property!, otherProperty!)) {
        if (options.change) {
            diffs[propertyName] = { change: { newValues: property!, oldValues: otherProperty! } };
        }
    }
}

export type LayerDiffOptions = {
    diffLayerChild?: boolean;
    ignorePos?: boolean;
    parentHierarchyId: string;
    matchedLayers?: BasicRecord<boolean>;
};

export default function getLayerDiffs(layer: Layer, otherLayer: Layer, options: LayerDiffOptions): LayerDiffs {
    const {
        rect, fills, borders, shadows, blur, rotation, borderRadius, cornerRadius,
        textStyles, content, opacity, blendMode, exportable, layoutGrow, layoutAlignment,
        layout, constraints, absoluteRect, bitmapImageHash, layerHashes, layers: children
    } = layer as InspectLayer;
    const {
        rect: otherRect, fills: otherFills, borders: otherBorders, shadows: otherShadows, blur: otherBlur,
        rotation: otherRotation, borderRadius: otherBorderRadius, cornerRadius: otherCornerRadius,
        textStyles: otherTextStyles, content: otherContent, opacity: otherOpacity, blendMode: otherBlendMode,
        exportable: otherExportable, layoutGrow: otherLayoutGrow, layoutAlignment: otherLayoutAlignment,
        layout: otherLayout, constraints: otherConstraints, absoluteRect: otherAbsoluteRect,
        bitmapImageHash: otherBitmapImageHash, layerHashes: otherLayerHashes,
        layers: otherChildren
    } = otherLayer as InspectLayer;

    const diffs = {} as LayerDiffs;

    layoutDiff(layout, otherLayout, diffs);
    constraintsDiff(constraints, otherConstraints, diffs);
    rectDiffWithLayoutAndConstraints(
        {
            rect: absoluteRect || rect,
            layout,
            constraints,
            childCount: children?.length || 0
        },
        {
            rect: otherAbsoluteRect || otherRect,
            layout: otherLayout,
            constraints: otherConstraints,
            childCount: otherChildren?.length || 0
        },
        options,
        diffs
    );
    fillsDiff(fills, otherFills, diffs);
    bordersDiff(borders, otherBorders, diffs);
    shadowsDiff(shadows, otherShadows, diffs);
    blurDiff(blur, otherBlur, diffs);
    rotationDiff(rotation, otherRotation, diffs);
    borderRadiusDiff(borderRadius, otherBorderRadius, diffs);
    cornerRadiusDiff(cornerRadius, otherCornerRadius, diffs);
    opacityDiff(opacity, otherOpacity, diffs);
    blendModeDiff(blendMode, otherBlendMode, diffs);
    contentDiff(content, otherContent, diffs);
    exportableDiff(exportable, otherExportable, diffs);
    bitmapImageHashDiff(bitmapImageHash, otherBitmapImageHash, diffs);
    layerHashesDiff(layerHashes, otherLayerHashes, diffs);
    textStylesDiff(textStyles, otherTextStyles, diffs);
    layoutAlignmentDiff(layoutAlignment, otherLayoutAlignment, diffs);
    layoutGrowDiff(layoutGrow, otherLayoutGrow, diffs);

    if (Object.keys(diffs).length) {
        diffs.prevLayer = otherLayer;
    }
    return diffs;
}
